import React, { useState, useEffect } from 'react';
import { ArrowLeft, BookOpen, Users, ExternalLink, Search } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const Libros = () => {
  const navigate = useNavigate();
  const { year } = useParams(); // Obtener el año de la URL
  const [todosLosLibros, setTodosLosLibros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Determinar el año actual o usar el parámetro
  const currentYear = year ? parseInt(year) : new Date().getFullYear();
  const availableYears = [2023, 2024, 2025];

  useEffect(() => {
    const fetchLibros = async () => {
      try {
  const response = await axios.get(`/data/libros.json?v=${Date.now()}`);
        setTodosLosLibros(response.data);
      } catch (error) {
        console.error('Error cargando libros:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLibros();
  }, []);

  // Filtrar libros por año
  const librosPorAnio = todosLosLibros.filter(libro => libro.year === currentYear);

  // Filtrar libros por búsqueda
  const librosFiltrados = librosPorAnio.filter(libro => {
    const searchLower = searchTerm.toLowerCase();
    return (
      libro.titulo.toLowerCase().includes(searchLower) ||
      (libro.subtitulo && libro.subtitulo.toLowerCase().includes(searchLower)) ||
      (libro.autores && libro.autores.some(autor => autor.toLowerCase().includes(searchLower))) ||
      (libro.coordinadores && libro.coordinadores.some(coord => coord.toLowerCase().includes(searchLower))) ||
      (libro.isbn_cenid && libro.isbn_cenid.toLowerCase().includes(searchLower)) ||
      (libro.isbn_esp && libro.isbn_esp.toLowerCase().includes(searchLower))
    );
  });

  // Función para resaltar texto
  const highlightText = (text, search) => {
    if (!search.trim() || !text) return text;
    
    const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 text-gray-900 rounded px-1">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Obtener el título descriptivo según el año
  const getYearDescription = () => {
    if (currentYear === new Date().getFullYear()) {
      return 'Publicaciones más recientes del CENID';
    } else if (currentYear === new Date().getFullYear() - 1) {
      return 'Publicaciones del año anterior';
    } else {
      return `Publicaciones históricas del CENID`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
              <BookOpen className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">
                Colección Editorial {currentYear}
              </h1>
              <p className="text-xl text-green-100">
                {getYearDescription()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Buscador */}
        <div className="mb-8 bg-white rounded-xl shadow-md p-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por título, autor, coordinador o ISBN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none text-gray-700"
            />
          </div>
          {searchTerm && (
            <div className="mt-3 text-sm text-gray-600">
              {librosFiltrados.length === 0 ? (
                <span className="text-red-600">No se encontraron resultados para "{searchTerm}"</span>
              ) : (
                <span>
                  Mostrando <span className="font-semibold text-[#0b742f]">{librosFiltrados.length}</span> {librosFiltrados.length === 1 ? 'resultado' : 'resultados'}
                </span>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 text-[#0b742f] mx-auto mb-4 animate-pulse" />
            <p className="text-gray-600 text-lg">Cargando libros...</p>
          </div>
        ) : librosPorAnio.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md p-12 text-center">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No hay publicaciones para el año {currentYear}
            </h3>
            <p className="text-gray-600 mb-6">
              Aún no se han registrado libros para este año. Por favor, consulte otros años disponibles.
            </p>
            <button
              onClick={() => navigate(`/coleccion/${new Date().getFullYear()}`)}
              className="text-[#0b742f] hover:underline font-semibold"
            >
              Ver publicaciones más recientes
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {librosFiltrados.length === 0 && searchTerm ? (
              <div className="bg-white rounded-2xl shadow-md p-12 text-center">
                <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No se encontraron resultados
                </h3>
                <p className="text-gray-600">
                  Intenta con otros términos de búsqueda o <button onClick={() => setSearchTerm('')} className="text-[#0b742f] hover:underline font-semibold">limpia el filtro</button> para ver todos los libros.
                </p>
              </div>
            ) : (
              librosFiltrados.map((libro) => (
                <article
                  key={libro.id}
                  className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300"
                >
                  <div className="md:flex">
                    {/* Portada */}
                    <div className="md:w-1/3 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center p-8">
                      {libro.portada ? (
                        <img 
                          src={libro.portada} 
                          alt={`Portada de ${libro.titulo}`}
                          className="w-full h-auto rounded-lg shadow-lg"
                        />
                      ) : (
                        <div className="text-center">
                          <BookOpen className="w-32 h-32 text-[#0b742f] mx-auto mb-4" />
                          <p className="text-sm text-gray-600">Portada en producción</p>
                        </div>
                      )}
                    </div>

                    {/* Información */}
                    <div className="md:w-2/3 p-8">
                      <div className="mb-4">
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">
                          {highlightText(libro.titulo, searchTerm)}
                        </h2>
                        {libro.subtitulo && (
                          <p className="text-xl text-gray-600 italic">
                            {highlightText(libro.subtitulo, searchTerm)}
                          </p>
                        )}
                      </div>

                      <div className="space-y-3 mb-6">
                        {/* Editorial */}
                        {libro.editorial && (
                          <div className="text-sm text-gray-600">
                            <span className="font-semibold text-gray-700">Editorial: </span>
                            <span>{libro.editorial}</span>
                          </div>
                        )}

                        {/* Edición */}
                        {libro.edicion && (
                          <div className="text-sm text-gray-600">
                            <span className="font-semibold text-gray-700">Edición: </span>
                            <span>{libro.edicion}</span>
                          </div>
                        )}

                        {/* Coordinadores si existen */}
                        {libro.coordinadores && libro.coordinadores.length > 0 && (
                          <div className="flex items-start gap-2">
                            <Users className="w-5 h-5 text-[#0b742f] mt-1 flex-shrink-0" />
                            <div>
                              <span className="font-semibold text-gray-700">Coordinadores: </span>
                              <span className="text-gray-600">
                                {libro.coordinadores.map((coord, idx) => (
                                  <span key={idx}>
                                    {idx > 0 && ', '}
                                    {highlightText(coord, searchTerm)}
                                  </span>
                                ))}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Autores */}
                        {libro.autores && libro.autores.length > 0 && (
                          <div className="flex items-start gap-2">
                            <Users className="w-5 h-5 text-[#0b742f] mt-1 flex-shrink-0" />
                            <div>
                              <span className="font-semibold text-gray-700">Autores: </span>
                              <span className="text-gray-600">
                                {libro.autores.map((autor, idx) => (
                                  <span key={idx}>
                                    {idx > 0 && ', '}
                                    {highlightText(autor, searchTerm)}
                                  </span>
                                ))}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* ISBNs y DOI */}
                        <div className="space-y-1">
                          {libro.isbn_obra && (
                            <div className="text-sm text-gray-600">
                              <span className="font-semibold text-gray-700">ISBN Obra: </span>
                              <span>{highlightText(libro.isbn_obra, searchTerm)}</span>
                            </div>
                          )}
                          {libro.isbn_cenid && (
                            <div className="text-sm text-gray-600">
                              <span className="font-semibold text-gray-700">ISBN México (CENID): </span>
                              <span>{highlightText(libro.isbn_cenid, searchTerm)}</span>
                            </div>
                          )}
                          {libro.isbn_esp && (
                            <div className="text-sm text-gray-600">
                              <span className="font-semibold text-gray-700">ISBN España (AEVA): </span>
                              <span>{highlightText(libro.isbn_esp, searchTerm)}</span>
                            </div>
                          )}
                                                    {libro.doi && (
                            <div className="text-sm text-gray-600">
                              <span className="font-semibold text-gray-700">DOI: </span>
                              <a
                                href={libro.doi}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#0b742f] hover:underline"
                              >
                                {libro.doi}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>

                      {libro.enlace && (
                        <div className="flex flex-wrap gap-3">
                          <a
                            href={libro.enlace}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-[#0b742f] hover:bg-[#085a24] text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-md"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Descargar PDF
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>
        )}

        {/* Nota informativa */}
        <div className="mt-12 bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-blue-500 rounded-lg p-6 shadow-md">
          <p className="text-gray-700 leading-relaxed">
            <strong className="text-blue-700">📚 Nota:</strong> Esta página muestra las publicaciones del año {currentYear}. 
            {librosPorAnio.length > 0 && ` Se encontraron ${librosPorAnio.length} ${librosPorAnio.length === 1 ? 'publicación' : 'publicaciones'}.`}
            {' '}Para consultar publicaciones de otros años, utilice los enlaces de navegación.
          </p>
        </div>

        {/* Navegación entre años */}
        <div className="mt-8 flex justify-center gap-4">
          {availableYears.map((yearOption) => {
            const isPrevious = yearOption < currentYear;
            const isNext = yearOption > currentYear;
            const isCurrent = yearOption === currentYear;
            
            return (
              <button
                key={yearOption}
                onClick={() => navigate(`/coleccion/${yearOption}`)}
                disabled={isCurrent}
                className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                  isCurrent
                    ? 'bg-[#0b742f] text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                {isPrevious && '← '}{yearOption}{isNext && ' →'}
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default Libros;
